/**
 * Interaction View Shortcut — phrase matcher for "show me the
 * interactions" / "analyze the binding" / "ligand view" requests.
 *
 * This is a DISPLAY shortcut, not a computational workflow. It sets
 * up the BMaps 3D view for visual interaction analysis — the step
 * a medicinal chemist takes between scoring and fragment growing.
 *
 * The matcher lives here in the knowledge module (no execution deps);
 * the actual UserActions calls live in ChatBot.jsx where App and
 * UserActions are available. The matcher returns a descriptor of
 * which display actions to execute, not a plan or workflow.
 *
 * @module gibbs/knowledge/interactionViewShortcut
 */

/**
 * Phrases that should trigger the interaction view setup.
 * Matched case-insensitively via substring search.
 */
// Keep TIGHT — canonical phrases only. The LLM handles fuzzy variants.
const INTERACTION_VIEW_PHRASES = [
    'show interactions',
    'ligand view',
    'interaction view',
    'show hydrogen bonds',
    // Conversational variants where the user inserts "me the" between
    // the verb and the noun. Routing-gap fix 2026-05-03 to catch the
    // §13 semantic-retrieval corpus phrasing "show me the interactions".
    'me the interactions',
    'me the hydrogen bonds',
];

/**
 * Check if a user message is asking for interaction analysis view setup.
 *
 * @param {string} userMessage
 * @returns {boolean}
 */
export function matchInteractionViewIntent(userMessage) {
    if (!userMessage || typeof userMessage !== 'string') return false;
    const lower = userMessage.toLowerCase();
    return INTERACTION_VIEW_PHRASES.some((p) => lower.includes(p));
}

/**
 * Build a descriptor of which display actions to execute.
 * ChatBot.jsx interprets this and calls the actual UserActions.
 *
 * @returns {Object} Display action descriptor
 */
export function buildInteractionViewSetup() {
    return {
        intent: 'display_setup',
        actions: [
            { action: 'SetView', args: ['ligand'] },
            { action: 'SetHBonds', args: ['normal'] },
            { action: 'SetPiPiStacking', args: [true] },
            { action: 'SetLigandSolvation', args: [true] },
        ],
        message: 'Switching to **ligand view** with interaction highlights:\n\n'
            + '- **Hydrogen bonds** (green dashes)\n'
            + '- **Pi-pi stacking** interactions\n'
            + '- **Ligand solvation** highlighting\n\n'
            + 'Look for strong interactions to preserve and weak/missing '
            + 'contacts where the compound could be improved. When you\'re '
            + 'ready to improve the compound, tell me which position to '
            + 'grow from — e.g. *"grow from the methyl at C7"* — and I\'ll '
            + 'search for fragments that extend into the nearby pocket.',
    };
}
